// Thin server-function wrapper for the Verified Card Registry. All runtime
// logic lives in imported *.server modules (server-fn splitting drops siblings).

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  loadRegistryPage,
  registrySummaryForListing,
  searchRegistryRecords,
} from "./registry-query.server";
import {
  adminClient,
  mergeRegistryRecords,
  splitRegistryRecord,
  appendTimelineNote,
} from "./registry-service.server";
import { runRegistryFraudChecks } from "./fraud.server";
import {
  backfillRegistryFromListings,
  checkListingAgainstRegistry,
  linkListingFromCertificate,
  requestRegistryReview,
  sellerRegistryLinks,
  unlinkListingFromRegistry,
} from "./registry-listing.server";
import {
  sellerRegistryDashboard,
  adminSearchRegistry,
  adminRegistryRecord,
  adminRegistryQueues,
  adminRegistryStats,
  adminForceReverify,
  adminResolveSignal,
} from "./registry-admin.server";

/** Publish-time registry check (mismatches, duplicates, revoked records). */
export const checkListingRegistry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        provider: z.string().max(16),
        certNumber: z.string().max(64),
        excludeListingId: z.string().uuid().nullable().optional(),
        listing: z
          .object({
            card_name: z.string().max(200).nullable().optional(),
            set_name: z.string().max(200).nullable().optional(),
            card_number: z.string().max(60).nullable().optional(),
            grade: z.string().max(40).nullable().optional(),
            language: z.string().max(40).nullable().optional(),
            variant: z.string().max(80).nullable().optional(),
            finish: z.string().max(80).nullable().optional(),
            serial_number: z.string().max(80).nullable().optional(),
          })
          .optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) =>
    checkListingAgainstRegistry({
      provider: data.provider,
      certNumber: data.certNumber,
      listing: data.listing,
      excludeListingId: data.excludeListingId ?? null,
    }),
  );

/** Link a listing the caller owns to its permanent registry record. */
export const linkListingRegistry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        listingId: z.string().uuid(),
        provider: z.string().max(16),
        certNumber: z.string().max(64),
        sellerConfirmed: z.boolean().optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) =>
    linkListingFromCertificate({
      listingId: data.listingId,
      sellerId: context.userId,
      provider: data.provider,
      certNumber: data.certNumber,
      sellerConfirmed: data.sellerConfirmed ?? false,
    }),
  );

export const unlinkListingRegistry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ listingId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) =>
    unlinkListingFromRegistry({ listingId: data.listingId, sellerId: context.userId }),
  );

export const getMyRegistryLinks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => sellerRegistryLinks(context.userId));

export const submitRegistryReviewRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        registryId: z.string().uuid(),
        listingId: z.string().uuid().nullable().optional(),
        message: z.string().trim().min(10).max(1000),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) =>
    requestRegistryReview({
      registryId: data.registryId,
      userId: context.userId,
      listingId: data.listingId ?? null,
      message: data.message,
    }),
  );

export const getRegistryRecord = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ provider: z.string().max(16), cert: z.string().max(64) }).parse(d),
  )
  .handler(async ({ data }) => loadRegistryPage(data.provider.toLowerCase(), data.cert));

export const searchRegistry = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({
        query: z.string().max(120).optional(),
        provider: z.string().max(16).optional(),
        grade: z.string().max(24).optional(),
        limit: z.number().int().min(1).max(50).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => searchRegistryRecords(data));

export const getListingRegistrySummary = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ listingId: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => registrySummaryForListing(data.listingId));

async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (!data) throw new Error("Forbidden");
}

export const adminMergeRegistry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ sourceId: z.string().uuid(), targetId: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return mergeRegistryRecords(await adminClient(), data.sourceId, data.targetId);
  });

export const adminSplitRegistry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({ registryId: z.string().uuid(), listingIds: z.array(z.string().uuid()).max(50) })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return splitRegistryRecord(await adminClient(), data.registryId, data.listingIds);
  });

export const adminSetRegistryStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        registryId: z.string().uuid(),
        status: z.enum(["active", "provisional", "revoked", "under_review"]),
        note: z.string().max(500).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const admin = await adminClient();
    await admin
      .from("verified_card_registry")
      .update({ status: data.status })
      .eq("id", data.registryId);
    await appendTimelineNote(
      admin,
      data.registryId,
      "bidnova",
      "status_change",
      data.note ?? `Registry status set to ${data.status}`,
    );
    return { ok: true };
  });

export const adminRunFraudChecks = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({ registryId: z.string().uuid(), listingId: z.string().uuid().nullable().optional() })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const signals = await runRegistryFraudChecks(await adminClient(), {
      registryId: data.registryId,
      listingId: data.listingId ?? null,
      userId: null,
      listing: null,
    });
    // Evidence is arbitrary JSON; serialise it for the RPC boundary.
    return signals.map((s) => ({ ...s, evidence: JSON.stringify(s.evidence) }));

  });

/** Admin: backfill existing verified listings into the registry. */
export const adminBackfillRegistry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({ dryRun: z.boolean().default(true), limit: z.number().int().min(1).max(200).default(50) })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return backfillRegistryFromListings({ dryRun: data.dryRun, limit: data.limit });
  });

// --- seller + admin dashboards ----------------------------------------------

/** Full seller registry dashboard for the signed-in seller. */
export const getSellerRegistryDashboard = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => sellerRegistryDashboard(context.userId));

export const adminRegistrySearch = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        query: z.string().max(120).optional(),
        provider: z.string().max(16).optional(),
        status: z.string().max(24).optional(),
        limit: z.number().int().min(1).max(100).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return adminSearchRegistry(data);
  });

export const adminGetRegistryRecord = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ registryId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return adminRegistryRecord(data.registryId);
  });

export const adminGetRegistryQueues = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as never);
    return adminRegistryQueues();
  });

export const adminGetRegistryStats = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as never);
    return adminRegistryStats();
  });

export const adminReverifyRegistry = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ registryId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return adminForceReverify(data.registryId, context.userId);
  });

export const adminResolveRegistrySignal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        signalId: z.string().uuid(),
        status: z.enum(["resolved", "dismissed"]),
        resolution: z.string().max(500).default(""),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    return adminResolveSignal({
      signalId: data.signalId,
      actorId: context.userId,
      status: data.status,
      resolution: data.resolution,
    });
  });
